import { decimalsFor, formatMoney, sumMoney } from '@mms/shared';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';
import type { LatestRate, Row } from '../api/types';
import { useAuth, useCan } from '../auth/AuthContext';
import { DATE_FORMAT, fmtDate } from '../ui/format';
import { useBaseCurrency, useLatestRates, useLookup } from '../ui/lookups';

/**
 * Dashboard (redesigned 2026-10-17): quick actions, a Needs attention list, the latest exchange rates (retail, wholesale, change, stale or not), the invoices of the
 * last 10 days (what was sent through dispatchers, or the value by currency in the base currency; as a chart or a table) and the outstanding balances of Cash,
 * Banks, Dispatchers, Agents and Sellers with a currency filter. Every figure comes from the same balance queries as the screens it links to.
 */

// ---------------------------------------------------------------- helpers (exported for the tests)

const SERIES_COLORS = ['var(--bs-primary)', 'var(--bs-orange, #fd7e14)', 'var(--bs-success)', 'var(--bs-info)', 'var(--bs-danger)', 'var(--bs-secondary)'];

export const compact = (n: number): string => {
  const a = Math.abs(n);
  if (a >= 1e6) return `${+(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${+(n / 1e3).toFixed(1)}K`;
  return String(+n.toFixed(0));
};

/** Rows of { date, currency, orders, amountInr, total } -> chart rows for both views (kept for the legacy summary). */
export function summariseOrders(rows: Row[]) {
  interface Day { date: string; inr: string; orders: number; byCurrency: Record<string, number> }
  const byDay = new Map<string, Day>();
  const currencies = new Set<string>();
  for (const r of rows) {
    const d: Day = byDay.get(r.date) ?? { date: r.date, inr: '0.00', orders: 0, byCurrency: {} };
    d.inr = sumMoney([d.inr, r.amountInr]); // exact, across currencies
    d.orders += r.orders;
    d.byCurrency[r.currency] = Number(r.total);
    currencies.add(r.currency);
    byDay.set(r.date, d);
  }
  const data = [...byDay.values()].map((d) => ({ label: dayjs(d.date).format('DD-MM'), inr: Number(d.inr), orders: d.orders, ...d.byCurrency }));
  return { data, currencies: [...currencies].sort() };
}

export interface DayPoint { date: string; label: string; orders: number; sent: number; byCurrency: Record<string, number>; total: number }

/**
 * One point per day. `sent` is what went through dispatchers in `sentCode` (the dispatcher currency with the most volume, INR in practice); `byCurrency` is each
 * invoice currency's total converted to the base currency at today's rates (units of the currency per 1 base), so currencies can be added together. A currency with
 * no rate is left out and named in `missing`.
 */
export function chartPoints(rows: Row[], rates: LatestRate[], baseCode: string | undefined) {
  const volume = new Map<string, number>();
  for (const r of rows) if (r.sentCurrency) volume.set(r.sentCurrency, (volume.get(r.sentCurrency) ?? 0) + Number(r.amountInr));
  const sentCode = [...volume.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'INR';
  const rateOf = new Map(rates.map((r) => [r.currencyCode, Number(r.rate1)]));
  const missing = new Set<string>();
  const currencies = new Set<string>();
  const days = new Map<string, DayPoint>();
  for (const r of rows) {
    const d: DayPoint = days.get(r.date) ?? { date: r.date, label: dayjs(r.date).format('DD'), orders: 0, sent: 0, byCurrency: {}, total: 0 };
    d.orders += r.orders;
    if (r.sentCurrency === sentCode) d.sent += Number(r.amountInr);
    const code = String(r.currency);
    const rate = code === baseCode ? 1 : rateOf.get(code);
    if (rate && rate > 0) {
      const v = Number(r.total) / rate;
      d.byCurrency[code] = (d.byCurrency[code] ?? 0) + v;
      d.total += v;
      currencies.add(code);
    } else missing.add(code);
    days.set(r.date, d);
  }
  return { days: [...days.values()].sort((a, b) => a.date.localeCompare(b.date)), sentCode, currencies: [...currencies].sort(), missing: [...missing].sort() };
}

const niceMax = (m: number): number => {
  if (m <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(m));
  const n = m / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
};

/** One balance line: a party (or Cash / a bank) in one currency. `alert` / `limit` are the dispatcher's low balance alert and the agent's credit limit. */
export interface Line { key: string; name: string; code: string; balance: string; alert?: string | null; limit?: string | null }
export type Kind = 'cash' | 'bank' | 'disp' | 'agent' | 'sell';

/** What a balance chip means: red for negative (what that means depends on the kind), amber for a dispatcher below its alert or an agent over its limit. */
export function flagOf(kind: Kind, line: Line, dp: number): { tone: '' | 'neg' | 'low'; tip: string } {
  const b = Number(line.balance);
  const fmt = (v: string) => formatMoney(v, dp, dp);
  if (b < 0) {
    return { tone: 'neg', tip: kind === 'bank' ? 'Overdrawn' : kind === 'sell' ? 'You owe the seller' : kind === 'disp' ? 'You owe the dispatcher' : kind === 'agent' ? 'You owe the agent' : 'Negative' };
  }
  if (kind === 'disp' && Number(line.alert) > 0 && b < Number(line.alert)) return { tone: 'low', tip: `Below the alert level of ${fmt(String(line.alert))}` };
  if (kind === 'agent' && Number(line.limit) > 0 && b > Number(line.limit)) return { tone: 'low', tip: `Over the credit limit of ${fmt(String(line.limit))}` };
  return { tone: '', tip: kind === 'sell' ? 'The seller owes you' : kind === 'disp' ? 'Held for you' : kind === 'agent' ? 'Owed to you' : '' };
}

export interface Outstanding { cash: Row[]; banks: Row[]; suppliers: Row[]; agents: Row[]; purchasers: Row[] }

export function toLines(data: Outstanding): Record<Kind, Line[]> {
  const line = (key: string, r: Row, extra: Partial<Line> = {}): Line => ({ key, name: String(r.name), code: String(r.currencyCode), balance: String(r.balance), ...extra });
  return {
    cash: (data.cash ?? []).map((r) => line(`cash-${r.currencyId}`, { ...r, name: `${r.currencyCode} cash` })),
    bank: data.banks.map((r) => line(`bank-${r.id}`, r)),
    disp: data.suppliers.map((r) => line(`disp-${r.supplierId}`, r, { alert: r.lowBalanceAlert as string | null })),
    agent: data.agents.map((r) => line(`agent-${r.agentId}`, r, { limit: r.creditLimit as string | null })),
    sell: data.purchasers.map((r) => line(`sell-${r.purchaserId}`, r)),
  };
}

export interface Attention { tone: 'warn' | 'danger'; text: string; menu: string; to: string; action: string }

/** The Needs attention list: stale rates, dispatchers overdrawn or below their alert, agents over their credit limit and overdrawn banks. */
export function buildAttention(rates: LatestRate[], lines: Record<Kind, Line[]> | null, dpOf: (code: string) => number, today = dayjs()): Attention[] {
  const out: Attention[] = [];
  for (const r of rates) {
    if (!dayjs(r.rateDate).isSame(today, 'day')) {
      out.push({ tone: 'warn', text: `${r.currencyCode} rate was not updated today (last ${fmtDate(r.rateDate)})`, menu: 'exchange-rates', to: '/masters/exchange-rates', action: 'Update rate' });
    }
  }
  if (!lines) return out;
  const fm = (l: Line, v = l.balance) => `${l.code} ${formatMoney(v, dpOf(l.code), dpOf(l.code))}`;
  for (const l of lines.disp) {
    if (Number(l.balance) < 0) out.push({ tone: 'danger', text: `You owe ${l.name} ${fm(l, String(-Number(l.balance)))}`, menu: 'fund-transfer', to: '/accounts/fund-transfer?new=1', action: 'Fund transfer' });
    else if (Number(l.alert) > 0 && Number(l.balance) < Number(l.alert)) {
      out.push({ tone: 'warn', text: `${l.name} holds ${fm(l)}, below its alert of ${formatMoney(String(l.alert), dpOf(l.code), dpOf(l.code))}`, menu: 'fund-transfer', to: '/accounts/fund-transfer?new=1', action: 'Top up' });
    }
  }
  for (const l of lines.agent) {
    if (Number(l.limit) > 0 && Number(l.balance) > Number(l.limit)) {
      out.push({ tone: 'warn', text: `${l.name} owes ${fm(l)}, over the limit of ${formatMoney(String(l.limit), dpOf(l.code), dpOf(l.code))}`, menu: 'receipt', to: '/accounts/receipt?new=1', action: 'Receipt' });
    }
  }
  for (const l of lines.bank) {
    if (Number(l.balance) < 0) out.push({ tone: 'danger', text: `${l.name} is overdrawn by ${fm(l, String(-Number(l.balance)))}`, menu: 'fund-transfer', to: '/accounts/fund-transfer?new=1', action: 'Fund transfer' });
  }
  return out;
}

// ---------------------------------------------------------------- pieces

function QuickActions() {
  const canInvoice = useCan('orders', 'create');
  const items = [
    { label: 'New invoice', icon: 'file-earmark-plus', to: '/orders/new', ok: canInvoice, primary: true },
    { label: 'Purchase', icon: 'cart-check', to: '/accounts/purchase?new=1', ok: useCan('purchase', 'create') },
    { label: 'Receipt / payment', icon: 'arrow-left-right', to: '/accounts/receipt?new=1', ok: useCan('receipt', 'create') },
    { label: 'Fund transfer', icon: 'send', to: '/accounts/fund-transfer?new=1', ok: useCan('fund-transfer', 'create') },
    { label: 'Expense', icon: 'receipt', to: '/accounts/expenses?new=1', ok: useCan('expenses', 'create') },
  ].filter((i) => i.ok);
  if (items.length === 0) return null;
  return (
    <div className="d-flex flex-wrap gap-2" aria-label="Quick actions">
      {items.map((i) => (
        <Link key={i.label} to={i.to} className={`btn btn-sm ${i.primary ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center gap-1`}>
          <i className={`bi bi-${i.icon}`} aria-hidden />{i.label}
        </Link>
      ))}
    </div>
  );
}

function Card({ icon, title, right, children, footer, className = '' }: { icon: string; title: ReactNode; right?: ReactNode; children: ReactNode; footer?: ReactNode; className?: string }) {
  return (
    <div className={`card h-100 ${className}`}>
      <div className="card-header d-flex flex-wrap align-items-center gap-2 py-2 fw-semibold">
        <i className={`bi bi-${icon} text-primary`} aria-hidden />{title}
        {right && <div className="ms-auto d-flex align-items-center gap-2 fw-normal">{right}</div>}
      </div>
      {children}
      {footer && <div className="card-footer small text-body-secondary py-2">{footer}</div>}
    </div>
  );
}

function NeedsAttention({ items }: { items: Attention[] }) {
  const can = useAuth().can;
  return (
    <Card icon="bell" title={<>Needs attention {items.length > 0 && <span className="badge text-bg-warning ms-1" data-testid="attention-count">{items.length} item{items.length === 1 ? '' : 's'}</span>}</>}>
      {items.length === 0 ? (
        <div className="text-center text-body-secondary py-3 small">Nothing needs attention.</div>
      ) : (
        <ul className="list-group list-group-flush" data-testid="attention-list">
          {items.map((x, i) => (
            <li key={i} className="list-group-item d-flex align-items-center gap-2 py-2 small">
              <i className={`bi ${x.tone === 'danger' ? 'bi-exclamation-octagon text-danger' : 'bi-exclamation-triangle text-warning'}`} aria-hidden />
              <span className="flex-grow-1">{x.text}</span>
              {can(x.menu, 'view') && <Link to={x.to} className="btn btn-sm btn-outline-secondary py-0">{x.action}</Link>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RatesCard() {
  const rates = useLatestRates();
  const base = useBaseCurrency();
  const canRates = useCan('exchange-rates', 'view');
  const today = dayjs();
  return (
    <Card
      icon="graph-up-arrow" title="Latest exchange rates"
      right={canRates ? <Link to="/masters/exchange-rates" className="btn btn-sm btn-outline-secondary py-0">Update all rates</Link> : undefined}
      footer={base ? `Rates are per 1 ${base.code} (base currency). Change is against the previous rate.` : undefined}
    >
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-0">
          <thead><tr><th>Currency</th><th className="text-end">Retail</th><th className="text-end">Wholesale</th><th className="text-end">Change</th><th>As of</th></tr></thead>
          <tbody>
            {rates.data?.length === 0 && <tr><td colSpan={5} className="text-center text-body-secondary py-3">No rates entered yet</td></tr>}
            {rates.data?.map((r) => {
              const change = r.prevRate1 && Number(r.prevRate1) > 0 ? ((Number(r.rate1) - Number(r.prevRate1)) / Number(r.prevRate1)) * 100 : null;
              const stale = !dayjs(r.rateDate).isSame(today, 'day');
              return (
                <tr key={r.currencyId}>
                  <td className="fw-semibold">{r.currencyCode}</td>
                  <td className="text-end amount">{formatMoney(r.rate1, 4, 8)}</td>
                  <td className="text-end amount">{formatMoney(r.rate2, 4, 8)}</td>
                  <td className={`text-end ${change === null ? 'text-body-tertiary' : change >= 0 ? 'text-success' : 'text-danger'}`}>
                    {change === null ? '—' : `${change >= 0 ? '▲ +' : '▼ '}${change.toFixed(2)}%`}
                  </td>
                  <td>
                    {stale ? <span className="badge text-bg-warning">Not today</span> : <span className="badge text-bg-success">Today</span>}
                    {stale && <div className="small text-body-secondary">{fmtDate(r.rateDate)}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SegButtons<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { v: T; text: ReactNode; aria?: string }[]; label: string }) {
  return (
    <div className="btn-group btn-group-sm" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.v} type="button" aria-label={o.aria} aria-pressed={value === o.v} onClick={() => onChange(o.v)}
          className={`btn ${value === o.v ? 'btn-primary' : 'btn-outline-primary'}`}>{o.text}</button>
      ))}
    </div>
  );
}

function InvoicesCard() {
  const q = useQuery({ queryKey: ['dashboard', 'orders-by-date'], queryFn: () => api.get<Row[]>('/dashboard/orders-by-date', { days: 10 }) });
  const rates = useLatestRates();
  const base = useBaseCurrency();
  const currencies = useLookup('currencies');
  const [mode, setMode] = useState<'sent' | 'currency'>('sent');
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [tip, setTip] = useState<{ i: number; x: number; y: number } | null>(null);
  const { days, sentCode, currencies: codes, missing } = chartPoints(q.data ?? [], rates.data ?? [], base?.code);
  const dpOf = (code: string) => { const c = currencies.data?.find((x) => x.code === code); return typeof c?.decimals === 'number' ? c.decimals : decimalsFor(code); };
  const baseCode = base?.code ?? 'OMR';
  const fmtBase = (n: number) => formatMoney(n.toFixed(dpOf(baseCode)), dpOf(baseCode), dpOf(baseCode));
  const fmtSent = (n: number) => formatMoney(n.toFixed(dpOf(sentCode)), dpOf(sentCode), dpOf(sentCode));

  const totalOrders = days.reduce((a, d) => a + d.orders, 0);
  const totalSent = days.reduce((a, d) => a + d.sent, 0);
  const values = days.map((d) => (mode === 'sent' ? d.sent : d.total));
  const max = niceMax(Math.max(0, ...values));
  const ticks = [0, 0.25, 0.5, 0.75, 1];

  const tipText = (i: number): ReactNode => {
    const d = days[i]!;
    return (
      <>
        <b>{fmtDate(d.date)}</b> · {d.orders} invoice{d.orders === 1 ? '' : 's'}<br />
        {mode === 'sent' ? <>{sentCode} sent {fmtSent(d.sent)}</> : (
          <>
            {codes.filter((c) => d.byCurrency[c]).map((c) => <div key={c}><i className="dash-dot" style={{ background: SERIES_COLORS[codes.indexOf(c) % SERIES_COLORS.length] }} />{c} {fmtBase(d.byCurrency[c]!)} {baseCode}</div>)}
            Total {fmtBase(d.total)} {baseCode}
          </>
        )}
      </>
    );
  };

  return (
    <Card
      icon="bar-chart" title="Invoices, last 10 days"
      right={<>
        <SegButtons label="Chart view" value={mode} onChange={setMode} options={[{ v: 'sent', text: `${sentCode} sent` }, { v: 'currency', text: 'By currency' }]} />
        <SegButtons label="Display" value={view} onChange={setView}
          options={[{ v: 'chart', text: <i className="bi bi-bar-chart" aria-hidden />, aria: 'Chart view' }, { v: 'table', text: <i className="bi bi-table" aria-hidden />, aria: 'Table view' }]} />
      </>}
      footer={mode === 'currency' ? `Value in ${baseCode} at today's rates, so the currencies can be added together.${missing.length ? ` No rate for ${missing.join(', ')}: not included.` : ''}` : `Total ${sentCode} sent through dispatchers each day. The last 10 days that had invoices.`}
    >
      <div className="card-body position-relative py-2">
        {q.isPending ? <div className="text-body-secondary">Loading…</div> : days.length === 0 ? (
          <div className="text-body-secondary text-center py-4">No invoices yet</div>
        ) : (
          <>
            <div className="d-flex flex-wrap gap-4 small text-body-secondary mb-2">
              <div><b className="d-block fs-6 text-body">{totalOrders}</b>Invoices</div>
              <div><b className="d-block fs-6 text-body">{sentCode} {compact(totalSent)}</b>Sent</div>
              <div><b className="d-block fs-6 text-body">{(totalOrders / days.length).toFixed(1)}</b>Per day</div>
            </div>
            {mode === 'currency' && view === 'chart' && (
              <div className="d-flex flex-wrap gap-3 small text-body-secondary mb-1">
                {codes.map((c, i) => <span key={c}><i className="dash-dot" style={{ background: SERIES_COLORS[i % SERIES_COLORS.length] }} />{c}</span>)}
              </div>
            )}
            {view === 'chart' ? (
              <div data-testid="invoice-chart">
                <div className="d-flex gap-2">
                  <div className="dash-yax" aria-hidden>{ticks.map((t) => <span key={t} style={{ bottom: `${t * 100}%` }}>{compact(max * t)}</span>)}</div>
                  <div className="dash-plot flex-grow-1" role="img" aria-label="Invoices over the last 10 days"
                    onMouseLeave={() => setTip(null)}>
                    {ticks.slice(1).map((t) => <div key={t} className="dash-gl" style={{ bottom: `${t * 100}%` }} />)}
                    <div className="dash-cols">
                      {days.map((d, i) => {
                        const segs = mode === 'sent' ? [[d.sent, SERIES_COLORS[0]!]] as [number, string][] : codes.map((c, k) => [d.byCurrency[c] ?? 0, SERIES_COLORS[k % SERIES_COLORS.length]!] as [number, string]);
                        const lastIdx = segs.reduce((acc, s, k) => (s[0] > 0 ? k : acc), -1);
                        return (
                          <div key={d.date} className="dash-col" data-testid="chart-col"
                            onMouseMove={(e) => { const r = e.currentTarget.closest('.card-body')!.getBoundingClientRect(); setTip({ i, x: e.clientX - r.left + 12, y: e.clientY - r.top - 10 }); }}>
                            {values[i]! > 0 && (
                              <div className="dash-bar" style={{ height: `${(values[i]! / max) * 100}%` }}>
                                {segs.map((s, k) => s[0] > 0 && <div key={k} className="dash-seg" style={{ flex: `${s[0]} 1 0`, background: s[1], borderRadius: k === lastIdx ? '4px 4px 0 0' : undefined }} />)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="dash-xax">{days.map((d) => <span key={d.date} className={dayjs(d.date).isSame(dayjs(), 'day') ? 'fw-semibold text-body' : ''}>{d.label}</span>)}</div>
              </div>
            ) : (
              <div className="table-responsive" data-testid="invoice-table">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Date</th><th className="text-end">Invoices</th><th className="text-end">{sentCode} sent</th>
                      {codes.map((c) => <th key={c} className="text-end">{c} ({baseCode})</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {days.map((d) => (
                      <tr key={d.date}>
                        <td>{fmtDate(d.date)}</td><td className="text-end">{d.orders}</td><td className="text-end amount">{fmtSent(d.sent)}</td>
                        {codes.map((c) => <td key={c} className="text-end amount">{fmtBase(d.byCurrency[c] ?? 0)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {tip && view === 'chart' && days[tip.i] && <div className="dash-tip" style={{ left: tip.x, top: tip.y }} role="tooltip">{tipText(tip.i)}</div>}
          </>
        )}
      </div>
    </Card>
  );
}

const CARDS: { kind: Kind; title: string; icon: string; note: string }[] = [
  { kind: 'cash', title: 'Cash in hand', icon: 'cash-coin', note: 'Counted from the cash screens' },
  { kind: 'bank', title: 'Banks', icon: 'bank', note: 'Red means overdrawn' },
  { kind: 'disp', title: 'Dispatchers', icon: 'truck', note: 'Held for you. Amber is below alert' },
  { kind: 'agent', title: 'Agents', icon: 'person-circle', note: 'Owed to you. Amber is over limit' },
  { kind: 'sell', title: 'Sellers', icon: 'person-check', note: 'Negative (red): you owe them' },
];

function OutstandingBalances({ lines }: { lines: Record<Kind, Line[]> }) {
  const currencies = useLookup('currencies');
  const [filter, setFilter] = useState('');
  const dpOf = (code: string) => { const c = currencies.data?.find((x) => x.code === code); return typeof c?.decimals === 'number' ? c.decimals : decimalsFor(code); };
  const codes = [...new Set(Object.values(lines).flat().map((l) => l.code))].sort();
  return (
    <section aria-labelledby="outstanding-h">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h2 id="outstanding-h" className="h5 mb-0">Outstanding balances</h2>
        <div className="ms-auto d-flex flex-wrap align-items-center gap-1" role="group" aria-label="Currency filter">
          <span className="small text-body-secondary me-1">Currency</span>
          {['', ...codes].map((c) => (
            <button key={c || 'all'} type="button" aria-pressed={filter === c} onClick={() => setFilter(c)}
              className={`btn btn-sm ${filter === c ? 'btn-primary' : 'btn-outline-secondary'}`}>{c || 'All'}</button>
          ))}
        </div>
      </div>
      <div className="row g-3 row-cols-1 row-cols-sm-2 row-cols-xl-3 row-cols-xxl-5">
        {CARDS.map((cd) => {
          const shown = lines[cd.kind].filter((l) => !filter || l.code === filter);
          const parties = new Map<string, Line[]>();
          for (const l of shown) parties.set(l.key, [...(parties.get(l.key) ?? []), l]);
          const totals = [...new Set(shown.map((l) => l.code))].sort().map((c) => {
            const dp = dpOf(c);
            return `${c} ${formatMoney(sumMoney(shown.filter((l) => l.code === c).map((l) => l.balance), 3), dp, dp)}`;
          });
          return (
            <div className="col" key={cd.kind}>
              <div className="card h-100" data-testid={`balances-${cd.kind}`}>
                <div className="card-header py-2 fw-semibold"><i className={`bi bi-${cd.icon} text-primary me-2`} aria-hidden />{cd.title}{filter ? ` (${filter})` : ''}</div>
                {parties.size === 0 ? <div className="text-center text-body-secondary py-3 small">None yet</div> : (
                  <ul className="list-group list-group-flush">
                    {[...parties.values()].map((ls) => (
                      <li key={ls[0]!.key} className="list-group-item d-flex justify-content-between gap-2 py-2 small">
                        <span className="fw-semibold">{ls[0]!.name}</span>
                        <span className="text-end">
                          {ls.map((l) => {
                            const dp = dpOf(l.code);
                            const f = flagOf(cd.kind, l, dp);
                            return (
                              <div key={l.code}>
                                <span className={`dash-chip ${f.tone}`} title={f.tip || undefined}>
                                  {f.tone === 'low' && <i className="bi bi-exclamation-triangle me-1" aria-hidden />}{l.code} {formatMoney(l.balance, dp, dp)}
                                </span>
                              </div>
                            );
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {totals.length > 0 && <div className="small text-body-secondary px-3 py-1 border-top bg-body-tertiary">Total: {totals.join(' · ')}</div>}
                <div className="card-footer small text-body-secondary py-1">{cd.note}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function DashboardPage() {
  const { me, can } = useAuth();
  const base = useBaseCurrency();
  const rates = useLatestRates();
  const currencies = useLookup('currencies');
  const showBalances = can('reports');
  // the attention list needs the balances too, so it reads the same cached query
  const balances = useQuery({ queryKey: ['dashboard', 'outstanding'], queryFn: () => api.get<Outstanding>('/dashboard/outstanding'), enabled: showBalances });
  const dpOf = (code: string) => { const c = currencies.data?.find((x) => x.code === code); return typeof c?.decimals === 'number' ? c.decimals : decimalsFor(code); };
  const attention = buildAttention(rates.data ?? [], balances.data ? toLines(balances.data) : null, dpOf);
  return (
    <div className="d-flex flex-column gap-3">
      <div>
        <h1 className="h4 mb-1 d-flex align-items-center gap-2"><i className="bi bi-speedometer2 text-primary" aria-hidden />Welcome, {me?.user.name ?? ''}</h1>
        <div className="small text-body-secondary mb-2">{dayjs().format(`dddd, ${DATE_FORMAT}`)}{base ? ` · Base currency ${base.code}` : ''}</div>
        <QuickActions />
      </div>
      {(attention.length > 0 || showBalances) && <NeedsAttention items={attention} />}
      <div className="row g-3">
        <div className="col-lg-6"><RatesCard /></div>
        {can('orders') && <div className="col-lg-6"><InvoicesCard /></div>}
      </div>
      {showBalances && balances.isPending && <div className="text-body-secondary">Loading balances…</div>}
      {showBalances && balances.data && <OutstandingBalances lines={toLines(balances.data)} />}
    </div>
  );
}
